import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import FaceAvatar from '../common/FaceAvatar';
import { useLanguage } from '../context/LanguageContext';

const { FiUsers, FiShield, FiActivity, FiCheck, FiLock, FiEye, FiEdit3, FiPlus } = FiIcons;

const CollaborativeTools = () => {
  const { t } = useLanguage();

  const ROLES = [
    { id: 'admin', name: t('collaboration.roles.admin'), icon: FiShield, color: 'text-indigo-600', bg: 'bg-indigo-50', permissions: [t('collaboration.permissions.full'), t('collaboration.permissions.financials'), t('collaboration.permissions.team'), t('collaboration.permissions.ticketEdit')] },
    { id: 'editor', name: t('collaboration.roles.editor'), icon: FiEdit3, color: 'text-violet-600', bg: 'bg-violet-50', permissions: [t('collaboration.permissions.content'), t('collaboration.permissions.guest'), t('collaboration.permissions.checkin'), t('collaboration.permissions.reports')] },
    { id: 'staff', name: t('collaboration.roles.staff'), icon: FiEye, color: 'text-emerald-600', bg: 'bg-emerald-50', permissions: [t('collaboration.permissions.checkinOnly'), t('collaboration.permissions.guestSearch'), t('collaboration.permissions.realtime')] }
  ];

  const ACTIVITIES = [
    { id: 1, user: 'Sarah K.', action: t('collaboration.updatedPrices'), time: `2m ${t('collaboration.ago')}`, avatarIndex: 4 },
    { id: 2, user: 'James L.', action: t('collaboration.invitedStaff'), time: `15m ${t('collaboration.ago')}`, avatarIndex: 6 },
    { id: 3, user: t('collaboration.system'), action: t('collaboration.autoScaled'), time: `1h ${t('collaboration.ago')}`, avatarIndex: null }
  ];

  const [selectedRole, setSelectedRole] = useState(ROLES[0]);

  // Handle case where role change doesn't update selected state cleanly when language changes
  const activeRole = ROLES.find(r => r.id === selectedRole.id) || ROLES[0];

  return (
    <section id="collaboration" className="py-24 bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side: Content */}
          <div className="order-1 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wider mb-6">
              <SafeIcon icon={FiUsers} className="w-4 h-4" /> {t('collaboration.badge')}
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">
              {t('collaboration.title')} <br />
              <span className="text-gradient">{t('collaboration.titleSpan')}</span>
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t('collaboration.desc')}
            </p>
            
            <ul className="space-y-6">
              {[
                { title: t('collaboration.featuresList.permTitle'), desc: t('collaboration.featuresList.permDesc') },
                { title: t('collaboration.featuresList.syncTitle'), desc: t('collaboration.featuresList.syncDesc') },
                { title: t('collaboration.featuresList.auditTitle'), desc: t('collaboration.featuresList.auditDesc') }
              ].map((item, i) => (
                <li key={i} className="flex gap-4">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 mt-1">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">{item.title}</h4>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-12">
              <button className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-2 hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10">
                {t('collaboration.setupCta')}
              </button>
            </div>
          </div>

          {/* Right Side: Interactive Workspace Mockup */}
          <div className="order-2 lg:order-2 relative">
            <div className="absolute -inset-10 bg-violet-500/5 rounded-full blur-3xl -z-10"></div>
            
            <div className="bg-slate-50 rounded-[32px] border border-slate-200 p-6 md:p-8 shadow-2xl relative">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <SafeIcon icon={FiUsers} className="text-indigo-600" /> {t('collaboration.titleWorkspace')}
                </h3>
                <button className="p-2 bg-white rounded-lg border border-slate-200 text-slate-400 hover:text-indigo-600 transition-colors">
                  <SafeIcon icon={FiPlus} />
                </button>
              </div>

              <div className="space-y-3 mb-8">
                {ROLES.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRole(role)}
                    className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all ${
                      activeRole.id === role.id 
                        ? 'bg-white border-indigo-200 shadow-lg shadow-indigo-500/5 ring-1 ring-indigo-500/10' 
                        : 'border-transparent hover:bg-white/60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${role.bg} ${role.color}`}>
                        <SafeIcon icon={role.icon} className="w-5 h-5" />
                      </div>
                      <span className="font-semibold text-slate-700">{role.name}</span>
                    </div>
                    {activeRole.id === role.id && <SafeIcon icon={FiCheck} className="text-indigo-600" />}
                  </button>
                ))}
              </div>

              <div className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">{t('collaboration.granularControls')}</div>
                <div className="grid grid-cols-2 gap-3">
                  {activeRole.permissions.map((perm, idx) => (
                    <motion.div 
                      key={perm}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg border border-slate-100"
                    >
                      <SafeIcon icon={FiLock} className="w-3 h-3 text-slate-400" /> {perm}
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Floating Activity Feed */}
              <motion.div 
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                className="absolute -right-6 -bottom-6 w-64 bg-slate-900 rounded-2xl p-5 shadow-2xl border border-slate-800 hidden md:block"
              >
                <div className="flex items-center gap-2 text-white text-xs font-bold mb-4">
                  <SafeIcon icon={FiActivity} className="text-emerald-400" /> {t('collaboration.liveActivity')}
                </div>
                <div className="space-y-4">
                  {ACTIVITIES.map((act) => (
                    <div key={act.id} className="flex gap-3">
                      {act.avatarIndex !== null ? (
                        <FaceAvatar index={act.avatarIndex} className="w-6 h-6 rounded-full border border-slate-700" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-[8px] text-indigo-400">AI</div>
                      )}
                      <div>
                        <p className="text-[10px] text-slate-300 leading-tight">
                          <span className="font-bold text-white">{act.user}</span> {act.action}
                        </p>
                        <span className="text-[8px] text-slate-500">{act.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollaborativeTools;